import React, { useCallback, useMemo, useState } from 'react';
import { ContactInformationFormView } from './ContactInformationFormView';
import { useFormik } from 'formik';
import { validationSchema } from './validationSchema';
import { User, ValidationResponse } from 'types';
import { createAddressObject, createCivilityObject, createPhoneObject } from 'utils';
import { useReturn } from 'hooks';
import { ROUTES } from 'routes';
import { useHistory } from 'react-router-dom';
import { NormalizedValidationService, PhoneValidationService, Tracking, UserManagementService } from 'api';
import { useDeposit } from 'providers';

interface Props {
  user: User;
}

export const ContactInformationForm: React.FC<Props> = ({ user }) => {
  const { verifiedPhone, setContacts } = useDeposit();
  const [errorMessage, setErrorMessage] = useState<string>();
  const history = useHistory();
  const defaultAddress = useMemo(() => (user.addresses ? user.addresses[0] : undefined), [user.addresses]);
  const defaultPhone = useMemo(() => (user.phones ? user.phones.find(phone => phone.type === 'SELLER') : undefined), [
    user.phones,
  ]);

  const {
    isSubmitting,
    errors,
    touched,
    values,
    handleSubmit,
    handleChange,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormik({
    initialValues: {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: defaultPhone ? defaultPhone.number : '',
      address: defaultAddress
        ? [
            defaultAddress.addressLine1,
            defaultAddress.addressLine2,
            defaultAddress.addressLine3,
            defaultAddress.addressLine4,
          ].join('')
        : '',
      country: defaultAddress ? defaultAddress.country : 'FR',
      city: defaultAddress ? defaultAddress.city : '',
      civility: user?.civility?.abbr,
      email: user.email,
      zipCode: defaultAddress ? defaultAddress.zipCode : '',
      geoLoc: defaultAddress?.geoLoc,
    },
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      let addresses;
      let phones;
      let phoneValidationResponse: ValidationResponse;

      try {
        phoneValidationResponse = await NormalizedValidationService.validatePhoneNumber(values.phone);
      } catch (e) {
        setFieldError('phone', (e as Error).message);
        Tracking.sendCurrentPageError();
        setSubmitting(false);
        return;
      }

      if (defaultPhone) {
        const index = user.phones?.findIndex(p => p.number === defaultPhone.number);
        phones = user.phones;
        if (phoneValidationResponse.nationalPhoneNumber !== phones![index!].number) {
          phones![index!].number = phoneValidationResponse.nationalPhoneNumber;
          phones![index!].validationDate = '';
        }
      } else {
        phones = [createPhoneObject(phoneValidationResponse.nationalPhoneNumber, 'SELLER', '')].concat(
          user.phones || [],
        );
      }

      const address = createAddressObject(
        values.address,
        values.zipCode,
        values.city,
        values.country,
        values.geoLoc!,
        defaultAddress ? defaultAddress.type : 'DEFAULT',
        defaultAddress ? defaultAddress.name : '',
      );
      if (defaultAddress) {
        addresses = [...(user.addresses || [])];
        addresses[0] = address;
      } else {
        addresses = [address].concat(user.addresses || []);
      }

      const userInfo: Partial<User> = {
        firstName: values.firstName,
        lastName: values.lastName,
        civility: createCivilityObject(values.civility),
        addresses,
        phones,
      };

      const shouldValidate = !(verifiedPhone === phoneValidationResponse.nationalPhoneNumber);
      //shouldValidatePhone(phoneValidationResponse.nationalPhoneNumber, defaultPhone);

      return UserManagementService.updateUser(user.id, userInfo)
        .then(async () => {
          if (shouldValidate) {
            await setContacts({}, values.firstName, values.lastName, values.zipCode);
            const requestId = await PhoneValidationService.requestCode(
              phoneValidationResponse.internationalPhoneNumber,
              user.userCorrelationId,
              phoneValidationResponse.lineType,
            );
            history.push(ROUTES.phoneValidation, {
              internationalPhoneNumber: phoneValidationResponse.internationalPhoneNumber,
              nationalPhoneNumber: phoneValidationResponse.nationalPhoneNumber,
              requestId,
              zipCode: values.zipCode,
              lineType: phoneValidationResponse.lineType,
            });
          } else {
            setContacts(
              { phone1: { value: phoneValidationResponse.nationalPhoneNumber } },
              values.firstName,
              values.lastName,
              values.zipCode,
            ).then(() => {
              history.push(ROUTES.vehiclePrice);
            });
          }
        })
        .catch(r => {
          setErrorMessage(r.message);
          Tracking.sendCurrentPageError();
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    validationSchema: validationSchema,
  });

  const onReturn = useReturn(ROUTES.photos);

  const handleTowns = useCallback(
    (latitude: number, longitude: number, towns: string[]) => {
      if (!towns.includes(values.city)) {
        setFieldValue('geoLoc', { latitude, longitude });
        setFieldValue('city', towns[0]);
      }
    },
    [values.city, setFieldValue],
  );

  return (
    <ContactInformationFormView
      setFieldValue={setFieldValue}
      setFieldError={setFieldError}
      setFieldTouched={setFieldTouched}
      errors={errors}
      handleTowns={handleTowns}
      onSubmit={handleSubmit}
      onChange={handleChange}
      touched={touched}
      values={values}
      onReturn={onReturn}
      isSubmitting={isSubmitting}
      errorMessage={errorMessage}
    />
  );
};
